import React, { useState, useEffect, useMemo } from 'react';
import {
    Modal,
    Box,
    Alert,
    Collapse,
    Divider,
    Text,
    Button,
    TextInput,
    Checkbox,
    Group,
    SegmentedControl,
    Tooltip,
    UnstyledButton,
    Paper
} from '@mantine/core';
import { IconAlertTriangle, IconChevronDown, IconChevronRight } from '@tabler/icons-react';
import { MODAL_SIZES } from '../../theme/mantineTheme';
import { entityService } from '../../services/entityServiceSingleton';
import { EntityTypeSelector } from '../common/EntityTypeSelector';
import { ConfigBuilder } from '../workflows/SettingsBuilder';
import { JsonDiffViewer } from '../common/JsonDiffViewer';
import { JsonViewer } from '../common/JsonViewer';
import { canonicalJsonString, getWorkflowConfigPayload, isJsonFieldReturnedAsString, JSON_FIELD_STRING_WARNING_MESSAGE } from '../../utils/workflowConfigUtils';
import { extractErrorMessage } from '../../utils/apiUtils';

import { ScimAceEditor } from '../common/ScimAceEditor';
import { DialogErrorAlert } from './DialogErrorAlert';
import { EntityDialogActions } from './EntityDialogActions';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import { useEntityDetailLoader } from '../../hooks/useEntityDetailLoader';
import { ShadcnUnsavedChangesDialog } from './ShadcnUnsavedChangesDialog';
import { LoadingState } from '../common/LoadingState';
import type { WorkflowStep } from '../workflows/types';

/** Read step configuration from API: Settings is current, Config is deprecated fallback. */
function getStepSettings(step: WorkflowStep | null | undefined): any {
    if (!step) return undefined;
    const raw = step.Settings;
    if (raw === undefined || raw === null) return undefined;
    if (typeof raw === 'object') return raw;
    const s = String(raw).trim();
    if (s === '' || s === '{}') return {};
    try {
        return JSON.parse(s);
    } catch {
        return raw;
    }
}

/** Get step settings as a JSON string for form/display. */
function getStepSettingsString(step: WorkflowStep | null | undefined): string {
    const val = getStepSettings(step);
    if (val === undefined || val === null) return '{}';
    if (typeof val === 'string') return val.trim() || '{}';
    return JSON.stringify(val, null, 2);
}

/** Default step shape for create / initial state. New API fields are merged in when loading. */
const DEFAULT_STEP: WorkflowStep = {
    DisplayName: '',
    Settings: '{}',
    Script: '',
    Action: false,
    Authorization: false,
    Request: false,
    EntityType: ''
};

interface WorkflowStepEditDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: (step: WorkflowStep) => void;
    step?: WorkflowStep | null;
    entityType?: string;
    storedCopy?: WorkflowStep | null;
    presentation?: 'modal' | 'inline';
}

export const WorkflowStepEditDialog: React.FC<WorkflowStepEditDialogProps> = ({
    open,
    onClose,
    onSuccess,
    step,
    entityType = 'WorkflowActivity',
    storedCopy,
    presentation = 'modal'
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [configMode, setConfigMode] = useState<'wizard' | 'json'>('wizard');
    const [showPayload, setShowPayload] = useState(false);
    const [showDiff, setShowDiff] = useState(false);

    const [formData, setFormData] = useState<WorkflowStep>({ ...DEFAULT_STEP });

    const [configError, setConfigError] = useState<string | null>(null);
    const [originalStep, setOriginalStep] = useState<WorkflowStep | null>(null);

    const isEditMode = !!step?.Id;

    // Resolve stub to full entity when opening from URL (edit by id); uses same tier3 attribute logic as view.
    const { fullEntity, isLoading: detailLoading, error: detailError } = useEntityDetailLoader({
        open,
        entity: (step as import('../../types/ScimEntity').ScimEntity) ?? null,
        entityType,
        selectedEntityId: step?.Id ?? null
    });
    const effectiveStep = fullEntity ?? step;

    // Generate a random GUID
    const generateGuid = (): string => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };

    // Generate ISO 8601 date/time stamp
    const generateTimestamp = (): string => {
        return new Date().toISOString();
    };

    // Helper to compare two config objects excluding editVersion and editDate
    const configHasChanges = (newConfig: any, originalConfig: any): boolean => {
        if (!originalConfig || Object.keys(originalConfig).length === 0) {
            // If there's no original config, consider it a change if new config has any non-metadata properties
            const newKeys = Object.keys(newConfig || {}).filter(
                key => key !== 'editVersion' && key !== 'editDate' && key !== 'version' && key !== 'updatedAt'
            );
            return newKeys.length > 0;
        }

        // Get all keys from both configs, excluding metadata
        const allKeys = new Set([
            ...Object.keys(newConfig || {}),
            ...Object.keys(originalConfig || {})
        ].filter(key => key !== 'editVersion' && key !== 'editDate' && key !== 'version' && key !== 'updatedAt'));

        // Compare each property
        for (const key of allKeys) {
            const newVal = newConfig?.[key];
            const origVal = originalConfig?.[key];

            // Normalize for comparison (handle JSON strings)
            const normalize = (val: any): string => {
                if (val === undefined || val === null) return '';
                if (typeof val === 'string') {
                    try {
                        // Try to parse and re-stringify to normalize JSON strings
                        const parsed = JSON.parse(val);
                        return JSON.stringify(parsed);
                    } catch {
                        return val.trim();
                    }
                }
                if (typeof val === 'object') {
                    return JSON.stringify(val);
                }
                return String(val);
            };

            if (normalize(newVal) !== normalize(origVal)) {
                return true; // Found a difference
            }
        }

        return false; // No changes found
    };

    // Helper to update Config metadata (editVersion and editDate) in the correct order
    const updateConfigMetadata = (newConfig: any, oldConfig?: any, forceNew: boolean = false): any => {
        try {
            const configObj = newConfig && typeof newConfig === 'object' ? newConfig : {};
            const originalConfigObj = oldConfig && typeof oldConfig === 'object' ? oldConfig : {};

            const shouldGenerateNew = forceNew ||
                !configObj.editVersion ||
                !configObj.editDate ||
                (oldConfig !== undefined && configHasChanges(configObj, originalConfigObj));

            const editVersion = shouldGenerateNew ? generateGuid() : (configObj.editVersion || generateGuid());
            const editDate = shouldGenerateNew ? generateTimestamp() : (configObj.editDate || generateTimestamp());

            const orderedConfig: any = {};

            if (configObj.displayName !== undefined) {
                orderedConfig.displayName = configObj.displayName;
            }

            orderedConfig.editVersion = editVersion;
            orderedConfig.editDate = editDate;

            Object.keys(configObj).forEach(key => {
                if (key !== 'displayName' && key !== 'editVersion' && key !== 'editDate' && key !== 'version' && key !== 'updatedAt') {
                    orderedConfig[key] = configObj[key];
                }
            });

            return orderedConfig;
        } catch (e) {
            return {
                editVersion: generateGuid(),
                editDate: generateTimestamp()
            };
        }
    };

    // Compare current step with stored copy to detect changes (use effectiveStep as loaded entity). Dynamic: all keys.
    const hasStepChanged = (): boolean => {
        if (!effectiveStep?.Id || !storedCopy) {
            return false;
        }
        const normalize = (val: any): string => {
            if (typeof val === 'string') return val.trim();
            if (typeof val === 'object' && val !== null) {
                try { return JSON.stringify(val); } catch { return String(val); }
            }
            return String(val ?? '');
        };
        const keys = new Set([...Object.keys(effectiveStep), ...Object.keys(storedCopy)]);
        keys.delete('Id');
        for (const key of keys) {
            const a = key === 'Settings' ? getStepSettingsString(effectiveStep) : (effectiveStep as Record<string, any>)[key];
            const b = key === 'Settings' ? getStepSettingsString(storedCopy) : (storedCopy as Record<string, any>)[key];
            if (normalize(a) !== normalize(b)) return true;
        }
        return false;
    };

    // Load step data when editing (read from Settings; fallback to deprecated Config). Use effectiveStep so stub-from-URL is resolved by useEntityDetailLoader.
    // Spread effectiveStep so any new API fields are included without hard-coding.
    useEffect(() => {
        if (open) {
            if (effectiveStep) {
                const stepData: WorkflowStep = { ...DEFAULT_STEP, ...effectiveStep, Settings: getStepSettingsString(effectiveStep) };
                setFormData(stepData);
                setOriginalStep({ ...stepData });
            } else {
                const newConfigObj = updateConfigMetadata({}, undefined, true);
                const newSettings = JSON.stringify(newConfigObj, null, 2);
                setFormData({ ...DEFAULT_STEP, Settings: newSettings });
                setOriginalStep(null);
            }
            setError(null);
            setConfigError(null);
            setShowPayload(false);
            setShowDiff(false);
        }
    }, [open, effectiveStep]);

    // Parse settings JSON for autocompletion hints
    const getConfigHints = (): any[] => {
        try {
            const configObj = formData.Settings ? (typeof formData.Settings === 'string' ? JSON.parse(formData.Settings) : formData.Settings) : {};
            return Object.keys(configObj).map(key => ({
                caption: key,
                snippet: `settings.${key}`,
                meta: `settings.${key} (${typeof configObj[key]})`,
                score: 1000
            }));
        } catch (e) {
            return [];
        }
    };

    // Get workflow-specific autocompletion hints
    const getWorkflowCompletions = (prefix: string): any[] => {
        const completions: any[] = [];
        const configHints = getConfigHints();
        const lowerPrefix = prefix.toLowerCase();

        const addIfMatches = (hints: Array<{ name: string; meta: string; snippet: string; score?: number }>, defaultScore: number = 1500) => {
            hints.forEach(hint => {
                if (hint.name.toLowerCase().includes(lowerPrefix) || prefix === '') {
                    completions.push({
                        caption: hint.name,
                        snippet: hint.snippet,
                        meta: hint.meta,
                        score: hint.score || defaultScore
                    });
                }
            });
        };

        if (prefix.startsWith('settings.') || prefix === 'settings') {
            completions.push(...configHints);

            if (prefix === 'settings') {
                completions.push({
                    caption: 'settings',
                    snippet: 'settings',
                    meta: 'Workflow step Settings object (JSON)',
                    score: 2000
                });
            }
        }

        addIfMatches([
            { name: 'step', meta: 'Current workflow step object', snippet: 'step' },
            { name: 'step.Id', meta: 'Current step ID', snippet: 'step.Id' },
            { name: 'step.DisplayName', meta: 'Current step display name', snippet: 'step.DisplayName' },
            { name: 'step.Settings', meta: 'Current step Settings (JSON)', snippet: 'step.Settings' },
            { name: 'step.Script', meta: 'Current step script', snippet: 'step.Script' },
            { name: 'workflow', meta: 'Parent workflow object', snippet: 'workflow' },
            { name: 'workflow.Id', meta: 'Workflow ID', snippet: 'workflow.Id' },
            { name: 'workflow.DisplayName', meta: 'Workflow display name', snippet: 'workflow.DisplayName' },
            { name: 'workflow.Steps', meta: 'Array of workflow steps', snippet: 'workflow.Steps' },
            { name: 'context', meta: 'Request context object', snippet: 'context' },
            { name: 'context.user', meta: 'Current user object', snippet: 'context.user' },
            { name: 'context.userId', meta: 'Current user ID', snippet: 'context.userId' },
            { name: 'context.requestId', meta: 'Unique request ID', snippet: 'context.requestId' },
            { name: 'context.timestamp', meta: 'Request timestamp', snippet: 'context.timestamp' },
            { name: 'context.correlationId', meta: 'Correlation ID for tracing', snippet: 'context.correlationId' },
            { name: 'entity', meta: 'Current entity being processed', snippet: 'entity' },
            { name: 'entity.Id', meta: 'Entity ID', snippet: 'entity.Id' },
            { name: 'entity.DisplayName', meta: 'Entity display name', snippet: 'entity.DisplayName' },
            { name: 'result', meta: 'Previous step result', snippet: 'result' }
        ], 1500);

        if (prefix.startsWith('getSettings') || prefix.startsWith('setSettings') || prefix.startsWith('hasSettings') || prefix.startsWith('validateSettings')) {
            addIfMatches([
                { name: 'getSettings()', meta: 'Get entire Settings object (parsed from JSON)', snippet: 'getSettings()' },
                { name: 'getSettingsValue()', meta: 'Get specific Settings value', snippet: 'getSettingsValue(${1:\'key\'})' },
                { name: 'setSettingsValue()', meta: 'Set Settings value (runtime modification)', snippet: 'setSettingsValue(${1:\'key\'}, ${2:value})' },
                { name: 'hasSettingsKey()', meta: 'Check if Settings key exists', snippet: 'hasSettingsKey(${1:\'key\'})' },
                { name: 'validateSettings()', meta: 'Validate Settings structure', snippet: 'validateSettings()' }
            ], 1700);
        }

        if (prefix.startsWith('getStep') || prefix.startsWith('isFirst') || prefix.startsWith('isLast')) {
            addIfMatches([
                { name: 'getStepIndex()', meta: 'Get current step index (0-based)', snippet: 'getStepIndex()' },
                { name: 'getStepCount()', meta: 'Get total number of steps in workflow', snippet: 'getStepCount()' },
                { name: 'isFirstStep()', meta: 'Check if this is the first step', snippet: 'isFirstStep()' },
                { name: 'isLastStep()', meta: 'Check if this is the last step', snippet: 'isLastStep()' },
                { name: 'getPreviousStepResult()', meta: 'Get result from previous step', snippet: 'getPreviousStepResult()' },
                { name: 'getStepResult()', meta: 'Get result from specific step by ID', snippet: 'getStepResult(${1:stepId})' },
                { name: 'getStepResultByIndex()', meta: 'Get result from step by index', snippet: 'getStepResultByIndex(${1:0})' },
                { name: 'getAllStepResults()', meta: 'Get all step results', snippet: 'getAllStepResults()' },
                { name: 'getNextStep()', meta: 'Get next step object', snippet: 'getNextStep()' },
                { name: 'getPreviousStep()', meta: 'Get previous step object', snippet: 'getPreviousStep()' },
                { name: 'getStep()', meta: 'Get step by ID', snippet: 'getStep(${1:stepId})' }
            ], 1700);
        }

        const commonPatterns = [
            { name: 'if', meta: 'If statement', snippet: 'if (${1:condition}) {\n\t${2:// code}\n}' },
            { name: 'for', meta: 'For loop', snippet: 'for (let ${1:i} = 0; ${1:i} < ${2:length}; ${1:i}++) {\n\t${3:// code}\n}' },
            { name: 'for...of', meta: 'For...of loop', snippet: 'for (const ${1:item} of ${2:array}) {\n\t${3:// code}\n}' },
            { name: 'for...in', meta: 'For...in loop', snippet: 'for (const ${1:key} in ${2:object}) {\n\t${3:// code}\n}' },
            { name: 'forEach', meta: 'Array forEach', snippet: '${1:array}.forEach((${2:item}) => {\n\t${3:// code}\n})' },
            { name: 'map', meta: 'Array map', snippet: '${1:array}.map((${2:item}) => ${3:item})' },
            { name: 'filter', meta: 'Array filter', snippet: '${1:array}.filter((${2:item}) => ${3:condition})' },
            { name: 'reduce', meta: 'Array reduce', snippet: '${1:array}.reduce((${2:acc}, ${3:item}) => ${4:acc + item}, ${5:0})' },
            { name: 'find', meta: 'Array find', snippet: '${1:array}.find((${2:item}) => ${3:condition})' },
            { name: 'some', meta: 'Array some', snippet: '${1:array}.some((${2:item}) => ${3:condition})' },
            { name: 'every', meta: 'Array every', snippet: '${1:array}.every((${2:item}) => ${3:condition})' },
            { name: 'async function', meta: 'Async function', snippet: 'async function ${1:name}(${2:params}) {\n\t${3:// code}\n}' },
            { name: 'arrow function', meta: 'Arrow function', snippet: 'const ${1:fn} = (${2:params}) => {\n\t${3:// code}\n}' },
            { name: 'try-catch', meta: 'Try-catch block', snippet: 'try {\n\t${1:// code}\n} catch (error) {\n\t${2:// handle error}\n}' },
            { name: 'try-catch-finally', meta: 'Try-catch-finally block', snippet: 'try {\n\t${1:// code}\n} catch (error) {\n\t${2:// handle error}\n} finally {\n\t${3:// cleanup}\n}' },
            { name: 'class', meta: 'Class definition', snippet: 'class ${1:ClassName} {\n\tconstructor(${2:params}) {\n\t\t${3:// code}\n\t}\n}' },
            { name: 'const', meta: 'Const declaration', snippet: 'const ${1:name} = ${2:value}' },
            { name: 'let', meta: 'Let declaration', snippet: 'let ${1:name} = ${2:value}' },
            { name: 'template literal', meta: 'Template literal', snippet: '`${1:text} ${2:${variable}}`' },
            { name: 'optional chaining', meta: 'Optional chaining', snippet: '${1:obj}?.${2:property}' },
            { name: 'nullish coalescing', meta: 'Nullish coalescing', snippet: '${1:value} ?? ${2:default}' },
            { name: 'JSON.parse()', meta: 'Parse JSON string (native)', snippet: 'JSON.parse(${1:jsonString})' },
            { name: 'JSON.stringify()', meta: 'Stringify object to JSON (native)', snippet: 'JSON.stringify(${1:obj})' },
            { name: 'Promise.all()', meta: 'Wait for all promises (native)', snippet: 'await Promise.all([${1:promise1}, ${2:promise2}])' },
            { name: 'Promise.race()', meta: 'Wait for first promise (native)', snippet: 'await Promise.race([${1:promise1}, ${2:promise2}])' }
        ];

        commonPatterns.forEach(pattern => {
            if (pattern.name.toLowerCase().includes(lowerPrefix) || prefix === '') {
                completions.push({
                    caption: pattern.name,
                    snippet: pattern.snippet,
                    meta: pattern.meta,
                    score: 500
                });
            }
        });

        return completions;
    };

    // Validate JSON config
    const validateConfig = (configString: string): boolean => {
        try {
            if (configString.trim() === '') {
                setConfigError(null);
                return true;
            }
            JSON.parse(configString);
            setConfigError(null);
            return true;
        } catch (e) {
            setConfigError(`Invalid JSON: ${e instanceof Error ? e.message : 'Unknown error'}`);
            return false;
        }
    };

    const handleSettingsChange = (value: string) => {
        setFormData({ ...formData, Settings: value });
        validateConfig(value);
    };

    // Unsaved changes: hook compares formData vs originalData over all keys (no schema = no hardcoded attribute list)
    const {
        hasUnsavedChanges,
        unsavedChangesDialogOpen,
        setUnsavedChangesDialogOpen,
        handleCloseAttempt,
        handleConfirmDiscard: handleConfirmDiscardBase
    } = useUnsavedChanges({
        isEditMode,
        formData: formData as Record<string, any>,
        originalData: originalStep as Record<string, any> | null,
        schemaAttributes: []
    });

    const handleClose = () => {
        if (!handleCloseAttempt()) return;
        onClose();
    };

    const handleConfirmDiscard = () => {
        handleConfirmDiscardBase(onClose);
    };

    const saveStep = async (closeAfterSave: boolean = true) => {
        const settingsStr = typeof formData.Settings === 'string' ? formData.Settings : JSON.stringify(formData.Settings ?? {});
        if (settingsStr && !validateConfig(settingsStr)) {
            setError('Please fix the JSON configuration errors before saving.');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            let savedStep: WorkflowStep;

            let newConfigObj: any = {};
            let oldConfigObj: any = undefined;
            const formSettingsStr = typeof formData.Settings === 'string' ? formData.Settings : JSON.stringify(formData.Settings ?? {});
            try {
                newConfigObj = formSettingsStr && formSettingsStr.trim() !== '' ? JSON.parse(formSettingsStr) : {};
            } catch (e) {
                newConfigObj = {};
            }
            if (isEditMode) {
                const existing = getStepSettings(effectiveStep);
                if (existing !== undefined && existing !== null) {
                    oldConfigObj = typeof existing === 'object' ? existing : (String(existing).trim() !== '' ? JSON.parse(String(existing)) : {});
                }
            }
            if (oldConfigObj === undefined) oldConfigObj = {};
            if (typeof oldConfigObj === 'string') {
                try {
                    oldConfigObj = JSON.parse(oldConfigObj);
                } catch {
                    oldConfigObj = {};
                }
            }
            if (typeof oldConfigObj !== 'object' || oldConfigObj === null) oldConfigObj = {};
            const updatedConfigObj = updateConfigMetadata(newConfigObj, oldConfigObj, false);
            const configWithMetadata = JSON.stringify(updatedConfigObj, null, 2);

            if (isEditMode && effectiveStep?.Id) {
                const operations: any[] = [];

                const configContentChanged = configHasChanges(newConfigObj, oldConfigObj);
                const settingsStrFromStep = getStepSettingsString(effectiveStep);
                const apiSentSettingsAsString = settingsStrFromStep.length > 2 && (settingsStrFromStep.trim().startsWith('{') || settingsStrFromStep.trim().startsWith('['));
                if (configContentChanged || apiSentSettingsAsString) {
                    const settingsPayload = getWorkflowConfigPayload(configWithMetadata);
                    operations.push({ op: 'replace', path: 'Settings', value: settingsPayload });
                }

                // Dynamic: any key in formData (except Id and Settings, which are handled above) gets a replace if changed
                const skipPaths = new Set(['Id', 'Settings']);
                for (const key of Object.keys(formData)) {
                    if (skipPaths.has(key)) continue;
                    const formVal = (formData as Record<string, any>)[key];
                    const origVal = (effectiveStep as Record<string, any>)[key];
                    const changed = JSON.stringify(formVal) !== JSON.stringify(origVal);
                    if (changed) {
                        operations.push({ op: 'replace', path: key, value: formVal });
                    }
                }

                if (operations.length > 0) {
                    savedStep = await entityService.patchEntity(effectiveStep.Id!, {
                        Operations: operations
                    }, entityType.toLowerCase());
                } else {
                    savedStep = effectiveStep;
                }
            } else {
                const createData: Record<string, any> = { ...formData };
                delete createData.Id;
                createData.Settings = getWorkflowConfigPayload(configWithMetadata);
                savedStep = await entityService.createEntity(entityType.toLowerCase(), createData);
            }

            if (savedStep && isEditMode) {
                // Use current form state as baseline so unsaved-changes check matches; copy all keys (dynamic for new fields)
                const updatedOriginalStep = { ...formData } as WorkflowStep;
                if (typeof updatedOriginalStep.Settings !== 'string') {
                    updatedOriginalStep.Settings = JSON.stringify(updatedOriginalStep.Settings ?? {});
                }
                setOriginalStep(updatedOriginalStep);
            }

            if (onSuccess && closeAfterSave) {
                onSuccess(savedStep);
            }

            if (closeAfterSave) {
                onClose();
            }
        } catch (err) {
            console.error('Error saving workflow activity:', err);
            setError(extractErrorMessage(err) || 'Failed to save workflow activity');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        await saveStep(true);
    };

    const handleApply = async () => {
        await saveStep(false);
    };

    // Compute original and current data for diff viewer (edit mode only)
    const { originalData, currentData, expectedPayload } = useMemo(() => {
        if (!isEditMode || !effectiveStep) {
            let newConfigObj: any = {};
            const formSettingsStr = typeof formData.Settings === 'string' ? formData.Settings : JSON.stringify(formData.Settings ?? {});
            try {
                newConfigObj = formSettingsStr && formSettingsStr.trim() !== '' ? JSON.parse(formSettingsStr) : {};
            } catch (e) {
                newConfigObj = {};
            }
            const parsedConfig = updateConfigMetadata(newConfigObj, undefined, true);
            const createPayload: any = {
                DisplayName: formData.DisplayName,
                Settings: parsedConfig,
                Script: formData.Script,
                Action: formData.Action ?? false,
                Authorization: formData.Authorization ?? false,
                Request: formData.Request ?? false
            };

            return {
                originalData: null,
                currentData: null,
                expectedPayload: createPayload
            };
        }

        let originalConfigObj: any = {};
        let currentConfigObj: any = {};
        try {
            const existingSettings = getStepSettings(effectiveStep);
            originalConfigObj = existingSettings !== undefined && existingSettings !== null && typeof existingSettings === 'object'
                ? existingSettings
                : (typeof existingSettings === 'string' && existingSettings.trim() !== '' ? JSON.parse(existingSettings) : {});
            const formStr = typeof formData.Settings === 'string' ? formData.Settings : JSON.stringify(formData.Settings ?? {});
            currentConfigObj = formStr && formStr.trim() !== '' ? JSON.parse(formStr) : {};
        } catch (e) {
            // If parsing fails, use empty objects
        }
        if (typeof originalConfigObj === 'string') {
            try {
                originalConfigObj = JSON.parse(originalConfigObj);
            } catch {
                originalConfigObj = {};
            }
        }
        if (typeof originalConfigObj !== 'object' || originalConfigObj === null) originalConfigObj = {};
        if (typeof currentConfigObj !== 'object' || currentConfigObj === null) currentConfigObj = {};

        const hasConfigChanges = configHasChanges(currentConfigObj, originalConfigObj);

        let configWithMetadata: string;
        if (hasConfigChanges) {
            const updatedConfigObj = updateConfigMetadata(currentConfigObj, originalConfigObj, false);
            configWithMetadata = JSON.stringify(updatedConfigObj, null, 2);
        } else {
            configWithMetadata = getStepSettingsString(effectiveStep);
        }

        const operations: any[] = [];

        const displayNameChanged = effectiveStep.DisplayName !== formData.DisplayName;
        if (displayNameChanged) {
            operations.push({ op: 'replace', path: 'DisplayName', value: formData.DisplayName });
        }

        // Only add Settings op when the payload would actually differ from the server (same shape + canonical compare)
        const baselinePayload = getWorkflowConfigPayload(getStepSettingsString(effectiveStep));
        const currentPayload = getWorkflowConfigPayload(configWithMetadata);
        const settingsActuallyChanged = canonicalJsonString(baselinePayload) !== canonicalJsonString(currentPayload);
        if (settingsActuallyChanged) {
            operations.push({ op: 'replace', path: 'Settings', value: currentPayload });
        }

        const scriptChanged = (effectiveStep.Script || '') !== (formData.Script || '');
        if (scriptChanged) {
            operations.push({ op: 'replace', path: 'Script', value: formData.Script });
        }

        const oldAction = effectiveStep.Action ?? false;
        const newAction = formData.Action ?? false;
        const actionChanged = oldAction !== newAction;
        if (actionChanged) {
            operations.push({ op: 'replace', path: 'Action', value: newAction });
        }

        const oldAuthorization = effectiveStep.Authorization ?? false;
        const newAuthorization = formData.Authorization ?? false;
        const authorizationChanged = oldAuthorization !== newAuthorization;
        if (authorizationChanged) {
            operations.push({ op: 'replace', path: 'Authorization', value: newAuthorization });
        }

        const oldRequest = effectiveStep.Request ?? false;
        const newRequest = formData.Request ?? false;
        const requestChanged = oldRequest !== newRequest;
        if (requestChanged) {
            operations.push({ op: 'replace', path: 'Request', value: newRequest });
        }

        const patchPayload = {
            Operations: operations
        };

        const originalConfigForDiff = originalConfigObj;

        let currentConfigForDiff: any;
        if (!hasConfigChanges) {
            currentConfigForDiff = originalConfigObj;
        } else {
            try {
                currentConfigForDiff = JSON.parse(configWithMetadata);
            } catch (e) {
                currentConfigForDiff = currentConfigObj;
            }
        }

        const current = {
            DisplayName: formData.DisplayName,
            Settings: currentConfigForDiff,
            Script: formData.Script,
            Action: formData.Action ?? false,
            Authorization: formData.Authorization ?? false,
            Request: formData.Request ?? false
        };

        const originalConfigForDiffDisplay = getStepSettings(effectiveStep);
        const originalSettingsDisplay = originalConfigForDiffDisplay != null && typeof originalConfigForDiffDisplay === 'object'
            ? originalConfigForDiffDisplay
            : (typeof originalConfigForDiffDisplay === 'string' ? originalConfigForDiffDisplay : originalConfigForDiff);

        const original = {
            DisplayName: effectiveStep.DisplayName,
            Settings: originalSettingsDisplay,
            Script: effectiveStep.Script || '',
            Action: effectiveStep.Action ?? false,
            Authorization: effectiveStep.Authorization ?? false,
            Request: effectiveStep.Request ?? false
        };

        const displayPayload = {
            Operations: patchPayload.Operations.map((op: any) => {
                if (op.path === 'Settings' && typeof op.value === 'string') {
                    try {
                        const parsed = JSON.parse(op.value);
                        return { ...op, value: parsed };
                    } catch (e) {
                        return op;
                    }
                }
                return op;
            })
        };

        return {
            originalData: original,
            currentData: current,
            expectedPayload: displayPayload
        };
    }, [isEditMode, effectiveStep, formData]);

    // Format JSON for display
    const formatConfig = () => {
        try {
            const raw = typeof formData.Settings === 'string' ? formData.Settings : JSON.stringify(formData.Settings ?? {});
            if (raw) {
                const parsed = JSON.parse(raw);
                setFormData({ ...formData, Settings: JSON.stringify(parsed, null, 2) });
                setConfigError(null);
            }
        } catch (e) {
            // Ignore formatting errors
        }
    };

    const formContent = (
        <>
            <DialogErrorAlert error={detailError ?? error} onClose={() => { setError(null); }} />
            {detailLoading && (
                <LoadingState variant="overlay" visible={true} />
            )}

            {hasStepChanged() && (
                <Alert
                    color="yellow"
                    icon={<IconAlertTriangle size={16} />}
                    mb="md"
                >
                    <Text size="sm" fw={600}>
                        Warning: This workflow activity has been modified since it was added to the workflow.
                    </Text>
                    <Text size="sm" mt="xs">
                        The current version differs from the copy stored in the workflow. Saving changes will update the stored copy.
                    </Text>
                </Alert>
            )}

            {isEditMode && effectiveStep && isJsonFieldReturnedAsString(effectiveStep.Settings, 'Settings') ? (
                <Alert color="yellow" mb="md">
                    {JSON_FIELD_STRING_WARNING_MESSAGE}
                </Alert>
            ) : null}

            <Box mt="md">
                <TextInput
                    label="Display Name"
                    value={formData.DisplayName || ''}
                    onChange={(e) => setFormData({ ...formData, DisplayName: e.currentTarget.value })}
                    onBlur={(e) => {
                        const trimmed = e.currentTarget.value.trim();
                        if (trimmed !== (formData.DisplayName || '')) setFormData({ ...formData, DisplayName: trimmed });
                    }}
                    required
                    mb="md"
                />

                <Group gap="lg" mb="md">
                    <Checkbox
                        label="Action"
                        checked={formData.Action ?? false}
                        onChange={(e) => setFormData({ ...formData, Action: e.currentTarget.checked })}
                    />
                    <Checkbox
                        label="Authorization"
                        checked={formData.Authorization ?? false}
                        onChange={(e) => setFormData({ ...formData, Authorization: e.currentTarget.checked })}
                    />
                    <Checkbox
                        label="Request"
                        checked={formData.Request ?? false}
                        onChange={(e) => setFormData({ ...formData, Request: e.currentTarget.checked })}
                    />
                </Group>

                {/* Script editor -- always visible */}
                <Text size="sm" fw={600} mb="xs">Script (JavaScript)</Text>
                <Paper withBorder bdrs={4} mb="lg" style={{ overflow: 'hidden' }}>
                    <ScimAceEditor
                        mode="javascript"
                        theme="monokai"
                        value={formData.Script || ''}
                        onChange={(value) => setFormData({ ...formData, Script: value })}
                        name="script-editor"
                        height="300px"
                        fontSize={14}
                        showPrintMargin={false}
                        enableAutocompletion={true}
                        enableSnippets={true}
                        enableLiveAutocompletion={true}
                        onLoad={async () => {
                            const langTools = await import('ace-builds/src-noconflict/ext-language_tools');
                            const customCompleter = {
                                getCompletions: (_editor: any, _session: any, _pos: any, prefix: string, callback: any) => {
                                    const completions = getWorkflowCompletions(prefix);
                                    callback(null, completions);
                                }
                            };
                            if (langTools.default && langTools.default.addCompleter) {
                                langTools.default.addCompleter(customCompleter);
                            }
                        }}
                        placeholder="// Enter your JavaScript code here"
                    />
                </Paper>

                <Divider mb="lg" />

                {/* Settings -- Wizard / JSON toggle */}
                <Group justify="space-between" align="center" mb="md">
                    <Text size="sm" fw={600}>Settings</Text>
                    <SegmentedControl
                        size="xs"
                        value={configMode}
                        onChange={(v) => setConfigMode(v as 'wizard' | 'json')}
                        data={[
                            { label: 'Wizard', value: 'wizard' },
                            { label: 'JSON Editor', value: 'json' },
                        ]}
                    />
                </Group>

                {configMode === 'wizard' ? (
                    <ConfigBuilder
                        value={typeof formData.Settings === 'string' ? (formData.Settings || '{}') : JSON.stringify(formData.Settings ?? {}, null, 2)}
                        onChange={(jsonString: string) => {
                            setFormData({ ...formData, Settings: jsonString });
                            validateConfig(jsonString);
                        }}
                        onError={(err: string | null) => setConfigError(err)}
                    />
                ) : (
                    <Box>
                        <Group justify="space-between" align="center" mb="xs">
                            <Text size="xs" c="dimmed">
                                Direct JSON Editing
                            </Text>
                            <Button size="sm" onClick={formatConfig}>
                                Format JSON
                            </Button>
                        </Group>
                        {configError && (
                            <Alert color="red" mb="xs">
                                {configError}
                            </Alert>
                        )}
                        <Paper withBorder mt="xs" bdrs={4} style={{ overflow: 'hidden' }}>
                            <ScimAceEditor
                                mode="json"
                                theme="monokai"
                                value={typeof formData.Settings === 'string' ? (formData.Settings || '{}') : JSON.stringify(formData.Settings ?? {}, null, 2)}
                                onChange={handleSettingsChange}
                                name="settings-editor"
                                height="400px"
                                fontSize={14}
                                showPrintMargin={true}
                                enableAutocompletion={true}
                                enableSnippets={true}
                                enableLiveAutocompletion={true}
                                placeholder='{"key": "value"}'
                            />
                        </Paper>
                    </Box>
                )}

                <Divider my="lg" />

                {/* Collapsible: Expected Payload */}
                <UnstyledButton onClick={() => setShowPayload((v) => !v)} w="100%">
                    <Group gap="xs" mb={showPayload ? 'sm' : 0}>
                        {showPayload ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                        <Text size="sm" fw={600}>
                            {isEditMode ? 'Expected PATCH Payload' : 'Expected CREATE Payload'}
                        </Text>
                    </Group>
                </UnstyledButton>
                <Collapse in={showPayload}>
                    <JsonViewer
                        data={expectedPayload}
                        title={isEditMode ? "Expected PATCH Payload" : "Expected CREATE Payload"}
                        description={isEditMode
                            ? "This is the PATCH request that will be sent to update the workflow activity."
                            : "This is the CREATE request that will be sent to create the workflow activity."
                        }
                        formatBodyMultiline
                    />
                </Collapse>

                {/* Collapsible: JSON Diff (edit mode only) */}
                {isEditMode && (
                    <>
                        <Divider my="sm" />
                        <UnstyledButton onClick={() => setShowDiff((v) => !v)} w="100%">
                            <Group gap="xs" mb={showDiff ? 'sm' : 0}>
                                {showDiff ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                                <Text size="sm" fw={600}>JSON Diff</Text>
                            </Group>
                        </UnstyledButton>
                        <Collapse in={showDiff}>
                            {originalData && currentData ? (
                                <JsonDiffViewer
                                    originalData={originalData}
                                    currentData={currentData}
                                    title="Workflow Activity Changes"
                                    excludedPaths={['Id', 'EntityType', 'Settings.editVersion', 'Settings.editDate']}
                                    collapsible={false}
                                    defaultExpanded={true}
                                />
                            ) : (
                                <Box p="md">
                                    <Text size="sm" c="dimmed">
                                        No changes detected
                                    </Text>
                                </Box>
                            )}
                        </Collapse>
                    </>
                )}
            </Box>

            <Box
                pt="md"
                mt="md"
                style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}
            >
                <Group justify="space-between" align="center" gap="md" wrap="wrap">
                {error ? (
                    <Alert
                        color="red"
                        withCloseButton
                        onClose={() => setError(null)}
                        flex="1 1 200px"
                        miw={0}
                        ml="md"
                    >
                        {error}
                    </Alert>
                ) : (
                    <Box flex="1 1 200px" miw={0} ml="md" />
                )}
                <EntityDialogActions
                    onCancel={handleClose}
                    onSave={handleSave}
                    onApply={handleApply}
                    loading={loading}
                    isEditMode={isEditMode}
                    hasChanges={hasUnsavedChanges}
                    isValid={!!formData.DisplayName && !configError}
                />
                </Group>
            </Box>
        </>
    );

    if (presentation === 'inline') {
        if (!open) return null;
        return (
            <>
                <Box>{formContent}</Box>
                <ShadcnUnsavedChangesDialog
                    open={unsavedChangesDialogOpen}
                    onClose={() => setUnsavedChangesDialogOpen(false)}
                    onConfirm={handleConfirmDiscard}
                    mode={isEditMode ? 'edit' : 'create'}
                    entityType="Workflow Activity"
                />
            </>
        );
    }

    return (
        <>
        <Modal
            opened={open}
            onClose={handleClose}
            title={<Tooltip label="WorkflowStepEditDialog" withArrow position="top"><span style={{ cursor: 'help' }}>{isEditMode ? 'Edit Workflow Activity' : 'Create Workflow Activity'}</span></Tooltip>}
            size={MODAL_SIZES.detail}
            styles={{
                content: { height: '90vh', maxHeight: '90vh', display: 'flex', flexDirection: 'column', position: 'relative' }
            }}
        >
            {formContent}
        </Modal>

        <ShadcnUnsavedChangesDialog
            open={unsavedChangesDialogOpen}
            onClose={() => setUnsavedChangesDialogOpen(false)}
            onConfirm={handleConfirmDiscard}
            mode={isEditMode ? 'edit' : 'create'}
            entityType="Workflow Activity"
        />
        </>
    );
};
